import { Alert, Box } from '@mui/material';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import {
  useWallContentStore,
  useBoardDrawingStore,
  useBoardEditingStore,
  useBoardSelectionStore,
  useBoardInteractionStore,
  useBoardViewportStore,
  useWallParticipantsStore,
  useWallStore,
  useWorkspaceUiStore,
} from '@walls/store';
import { WorkspaceActions } from './actions/WorkspaceActions';
import { useWorkspaceAccess } from './hooks/useWorkspaceAccess';
import { useBoardWebsocket } from './hooks/useBoardWebsocket';
import { useWorkspaceClipboard } from './hooks/useWorkspaceClipboard';
import { ParticipantsPanel } from './participants/ParticipantsPanel';
import { DotstormingPageLoader } from './shell/DotstormingPageLoader';
import { WallBoardSurface } from './shell/WallBoardSurface';
import { WorkspaceHeader } from './shell/WorkspaceHeader';
import { WorkspaceJoinPanel } from './shell/WorkspaceJoinPanel';
import { WorkspaceOverlays } from './shell/WorkspaceOverlays';
import { WallPasswordDialog } from './shell/WallPasswordDialog';
import { PageTools } from './tools/PageTools';
import { shouldShowWorkspaceLoadingState } from './workspaceLoadingState';

export function WorkspacePage() {
  const { wallId } = useParams();
  const board = useWallStore((state) => state.board);
  const topicKey = useWallStore((state) => state.topicKey);
  const authenticationBoard = useWallStore((state) => state.authenticationBoard);
  const requiresAuthentication = useWallStore((state) => state.requiresAuthentication);
  const isAuthenticatingWall = useWallStore((state) => state.isAuthenticatingWall);
  const authenticationError = useWallStore((state) => state.authenticationError);
  const checkWallAuthentication = useWallStore((state) => state.checkWallAuthentication);
  const authenticateWall = useWallStore((state) => state.authenticateWall);
  const wallLoadError = useWallStore((state) => state.wallLoadError);
  const loadWall = useWallStore((state) => state.loadWall);
  const resetWall = useWallStore((state) => state.resetWall);
  const isLoadingBoardContent = useWallContentStore((state) => state.isLoadingBoardContent);
  const boardContentLoadError = useWallContentStore((state) => state.boardContentLoadError);
  const hydrateParticipants = useWallParticipantsStore((state) => state.hydrateFromBoard);
  const resetParticipants = useWallParticipantsStore((state) => state.resetParticipants);
  const resetBoardDrawing = useBoardDrawingStore((state) => state.resetBoardDrawing);
  const resetBoardEditing = useBoardEditingStore((state) => state.resetBoardEditing);
  const resetBoardInteraction = useBoardInteractionStore((state) => state.resetBoardInteraction);
  const resetBoardSelection = useBoardSelectionStore((state) => state.resetBoardSelection);
  const resetBoardViewport = useBoardViewportStore((state) => state.resetBoardViewport);
  const participantPanelOpen = useWorkspaceUiStore((state) => state.participantPanelOpen);
  const resetWorkspaceUi = useWorkspaceUiStore((state) => state.resetWorkspaceUi);
  const { canShowPageTools, canUseWorkspaceTools, shouldShowJoinPanel } = useWorkspaceAccess(board);

  useEffect(() => {
    if (!wallId) {
      resetWall();
      resetParticipants();
      resetBoardDrawing();
      resetBoardEditing();
      resetBoardInteraction();
      resetBoardSelection();
      resetBoardViewport();
      resetWorkspaceUi();
      return;
    }

    resetBoardDrawing();
    resetBoardEditing();
    resetBoardInteraction();
    resetBoardSelection();
    resetBoardViewport();

    let cancelled = false;

    void (async () => {
      const wallRequiresAuthentication = await checkWallAuthentication(wallId);

      if (!cancelled && !wallRequiresAuthentication) {
        await loadWall(wallId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    checkWallAuthentication,
    loadWall,
    resetBoardDrawing,
    resetBoardEditing,
    resetBoardInteraction,
    resetBoardSelection,
    resetBoardViewport,
    resetParticipants,
    resetWall,
    resetWorkspaceUi,
    wallId,
  ]);

  useEffect(() => {
    if (board) {
      hydrateParticipants(board);
    }
  }, [board, hydrateParticipants]);

  useWorkspaceClipboard({ enabled: canUseWorkspaceTools });
  useBoardWebsocket(board);

  if (!wallId) {
    return <WorkspaceEmptyState />;
  }

  if (requiresAuthentication) {
    return (
      <>
        <WorkspacePasswordState />
        <WallPasswordDialog
          board={authenticationBoard}
          boardId={wallId}
          error={authenticationError}
          isAuthenticating={isAuthenticatingWall}
          open
          onAuthenticate={authenticateWall}
        />
      </>
    );
  }

  if (
    shouldShowWorkspaceLoadingState({
      board,
      wallLoadError,
    })
  ) {
    return <WorkspaceLoadingState />;
  }

  if (!board) {
    return <WorkspaceErrorState message={wallLoadError ?? 'Wall not loaded'} />;
  }

  return (
    <Box
      component="main"
      sx={{
        bgcolor: '#f9faf9',
        height: '100vh',
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
      }}
    >
      <WorkspaceHeader board={board} />
      {canUseWorkspaceTools && (
        <WorkspaceActions board={board} showHandTool={board.layoutStyle === 'open'} />
      )}
      {shouldShowJoinPanel && <WorkspaceJoinPanel board={board} topicKey={topicKey} />}
      <WallBoardSurface board={board} />
      {canShowPageTools && (
        <PageTools
          allowCardCreation={board.allowCardCreation}
          allowClustering={board.allowClustering}
          allowDrawingTools={board.allowDrawingTools}
          layoutStyle={board.layoutStyle}
        />
      )}
      {canUseWorkspaceTools && participantPanelOpen && <ParticipantsPanel board={board} />}
      {(isLoadingBoardContent || boardContentLoadError) && (
        <Box
          sx={{
            bottom: 20,
            left: '50%',
            position: 'fixed',
            transform: 'translateX(-50%)',
            zIndex: 1500,
          }}
        >
          {isLoadingBoardContent && <Alert severity="info">Loading wall content...</Alert>}
          {boardContentLoadError && <Alert severity="error">{boardContentLoadError}</Alert>}
        </Box>
      )}
      <WorkspaceOverlays board={board} topicKey={topicKey} />
    </Box>
  );
}

function WorkspaceErrorState({ message }: { message: string }) {
  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 3 }}>
      <Alert severity="error">{message}</Alert>
    </Box>
  );
}

function WorkspaceEmptyState() {
  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 3 }}>
      <Alert severity="info">Open a wall URL to load a workspace.</Alert>
    </Box>
  );
}

function WorkspaceLoadingState() {
  return (
    <Box
      component="main"
      sx={{
        alignItems: 'center',
        bgcolor: '#f9faf9',
        display: 'flex',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
      }}
    >
      <DotstormingPageLoader />
    </Box>
  );
}

function WorkspacePasswordState() {
  return (
    <Box
      component="main"
      sx={{
        bgcolor: '#f9faf9',
        minHeight: '100vh',
        width: '100%',
      }}
    />
  );
}
